// The text that meaning search indexes: one entry per record, language and section of text
import type { ArticleNode, KBStore } from "../../articles/types"
import type { Provider, Service, ServiceStore } from "../../service-map/types"
import type { SiteConfigStore } from "../../site-config/types"
import type { PageContent } from "../../site-config/pageContent"

export type SearchArea = "articles" | "services" | "site"

export interface IndexSection {
  area: SearchArea
  kind: "article" | "service" | "provider" | "block"
  recordId: string
  locale: string
  chunk: number
  content: string
  hash: string
}

// Target section length, and the most sections one record language gets
export const CHUNK_CHARS = 1500
export const MAX_CHUNKS = 12

export const sectionKey = (s: Pick<IndexSection, "area" | "kind" | "recordId" | "locale" | "chunk">) =>
  `${s.area}|${s.kind}|${s.recordId}|${s.locale}|${s.chunk}`

export function plainText(html: unknown): string {
  if (typeof html !== "string") return ""
  return html
    .replace(/<br\s*\/?>/gi, "\n")
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, "\n")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/[ \t]+/g, " ")
    .replace(/ *\n */g, "\n")
    .replace(/\n{3,}/g, "\n\n")
    .trim()
}

// FNV-1a over the text, plus its length: tells whether a section changed since it was embedded
export function contentHash(text: string): string {
  let h = 0x811c9dc5
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 0x01000193)
  }
  return `${(h >>> 0).toString(16)}-${text.length}`
}

// Splits text on paragraphs, then sentences, into sections of about CHUNK_CHARS, each headed by the title
export function chunkText(title: string, body: string): string[] {
  const head = title.trim()
  const text = body.trim()
  if (!text) return head ? [head] : []

  const pieces: string[] = []
  for (const para of text.split(/\n+/)) {
    if (para.length <= CHUNK_CHARS) { pieces.push(para); continue }
    // A long paragraph splits at sentence ends, and a long sentence at the limit
    for (const sentence of para.split(/(?<=[.!?。؟])\s+/)) {
      for (let i = 0; i < sentence.length; i += CHUNK_CHARS) pieces.push(sentence.slice(i, i + CHUNK_CHARS))
    }
  }

  const chunks: string[] = []
  let current = ""
  for (const piece of pieces) {
    if (current && current.length + piece.length + 1 > CHUNK_CHARS) {
      chunks.push(current)
      current = ""
    }
    current = current ? `${current}\n${piece}` : piece
  }
  if (current) chunks.push(current)

  return chunks.slice(0, MAX_CHUNKS).map(c => (head ? `${head}\n\n${c}` : c))
}

function push(out: IndexSection[], base: Omit<IndexSection, "chunk" | "content" | "hash">, title: string, body: string) {
  chunkText(title, body).forEach((content, chunk) => {
    out.push({ ...base, chunk, content, hash: contentHash(content) })
  })
}

const lines = (...parts: (string | undefined | null | false)[]) => parts.filter(Boolean).join("\n")

// One record's sections, in every language it has text in
export function articleRecordSections(a: ArticleNode): IndexSection[] {
  const out: IndexSection[] = []
  for (const [locale, t] of Object.entries(a.translations ?? {})) {
    push(out, { area: "articles", kind: "article", recordId: a.id, locale }, (t?.title ?? "").trim(),
      lines(plainText(t?.description), plainText(t?.body)))
  }
  return out
}

export function providerRecordSections(p: Provider): IndexSection[] {
  const out: IndexSection[] = []
  for (const [locale, t] of Object.entries(p.translations ?? {})) {
    push(out, { area: "services", kind: "provider", recordId: p.id, locale }, (t?.name || p.name || "").trim(),
      lines(plainText(t?.description), t?.address && `Address: ${t.address}`))
  }
  return out
}

export function serviceRecordSections(s: Service, store: ServiceStore): IndexSection[] {
  const out: IndexSection[] = []
  const provider = s.providerId ? store.providers[s.providerId] : undefined
  for (const [locale, t] of Object.entries(s.translations ?? {})) {
    const providerName = provider?.translations?.[locale]?.name || provider?.name
    push(out, { area: "services", kind: "service", recordId: s.id, locale }, (t?.name || s.name || "").trim(),
      lines(plainText(t?.description), t?.address && `Address: ${t.address}`, providerName && `Provider: ${providerName}`))
  }
  return out
}

// Prose only: handles, links and addresses have no spaces and don't carry meaning
function proseOf(record: Record<string, unknown> | undefined, skip: Set<string>): string[] {
  if (!record) return []
  const out: string[] = []
  for (const [k, v] of Object.entries(record)) {
    if (skip.has(k) || typeof v !== "string") continue
    const text = plainText(v)
    if (text.includes(" ")) out.push(text)
  }
  return out
}

const BLOCK_SKIP = new Set(["id", "type", "title", "bgColor", "textColor", "locale", "translations"])

export function blockRecordSections(b: PageContent, defaultLocale: string): IndexSection[] {
  const out: IndexSection[] = []
  const translations = (b.translations ?? {}) as unknown as Record<string, Record<string, unknown>>
  for (const locale of new Set([...Object.keys(translations), defaultLocale])) {
    const text = [...proseOf(translations[locale], BLOCK_SKIP)]
    // The block's own fields hold its source-language text
    if (locale === defaultLocale) {
      for (const t of proseOf(b as unknown as Record<string, unknown>, BLOCK_SKIP)) if (!text.includes(t)) text.push(t)
    }
    if (!text.length) continue
    push(out, { area: "site", kind: "block", recordId: b.id, locale }, (b.title ?? "").trim(), text.join("\n"))
  }
  return out
}

// A record's sections, reused while it's the same object (store updates copy only what changes).
// `deps` are other records its text reads, like a service's provider
const sectionCache = new WeakMap<object, { deps: unknown[]; sections: IndexSection[] }>()
function cached(record: object, deps: unknown[], build: () => IndexSection[]): IndexSection[] {
  const hit = sectionCache.get(record)
  if (hit && hit.deps.length === deps.length && hit.deps.every((d, i) => d === deps[i])) return hit.sections
  const sections = build()
  sectionCache.set(record, { deps, sections })
  return sections
}

export const articleSections = (store: KBStore) =>
  Object.values(store.articles).flatMap(a => cached(a, [], () => articleRecordSections(a)))

export const serviceSections = (store: ServiceStore) => [
  ...Object.values(store.providers).flatMap(p => cached(p, [], () => providerRecordSections(p))),
  ...Object.values(store.services).flatMap(s =>
    cached(s, [s.providerId ? store.providers[s.providerId] : null], () => serviceRecordSections(s, store))),
]

export const siteSections = (store: SiteConfigStore) =>
  Object.values(store.pageContents).flatMap(b => cached(b, [store.defaultLocale], () => blockRecordSections(b, store.defaultLocale)))
